import React from 'react'

/**
 * 20-px line icons of the organizer side menu (mock `_kit/orgside.html`, convention `_ic`: 24 × 24
 * viewBox, stroke 2, round caps). The trophy stands for Leaderboard — never a podium, the charter
 * forbids redrawing the logo. Spectators / Live Judging / Qualification / Reviews are ours.
 */
const svg = (children, props = {}) => (
    <svg
        className="osd-ico"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        {...props}
    >
        {children}
    </svg>
)

export const ORG_MENU_ICONS = {
    help: () =>
        svg(
            <>
                <circle cx="12" cy="12" r="9" />
                <path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.24c-.7.35-1.1.9-1.1 1.66v.3" />
                <path d="M12 17h.01" />
            </>,
        ),
    eventPage: () =>
        svg(
            <>
                <rect x="4" y="3.5" width="16" height="17" rx="2" />
                <path d="M4 8.5h16M8 12.5h8M8 16h5" />
            </>,
        ),
    registrations: () =>
        svg(
            <>
                <path d="M3.5 9V6.5a1 1 0 0 1 1-1h15a1 1 0 0 1 1 1V9a3 3 0 0 0 0 6v2.5a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1V15a3 3 0 0 0 0-6z" />
                <path d="M14.5 5.5v2M14.5 11v2M14.5 16.5v2" />
            </>,
        ),
    volunteers: () =>
        svg(
            <>
                <circle cx="9" cy="8" r="3.2" />
                <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
                <path d="M15.5 5.3a3.2 3.2 0 0 1 0 5.4M17 14.2a5.5 5.5 0 0 1 3.5 5.3" />
            </>,
        ),
    spectators: () =>
        svg(
            <>
                <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" />
                <circle cx="12" cy="12" r="3" />
            </>,
        ),
    emailing: () =>
        svg(
            <>
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3 7 9 6 9-6" />
            </>,
        ),
    planning: () =>
        svg(
            <>
                <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
                <path d="M8 3v4M16 3v4M3.5 10h17" />
            </>,
        ),
    liveJudging: () =>
        svg(
            <>
                <circle cx="12" cy="13.5" r="7" />
                <path d="M12 9.5v4l2.5 1.5M9.5 3h5M12 3v3.5M18 7l1.5-1.5" />
            </>,
        ),
    leaderboard: () =>
        svg(
            <>
                <path d="M7 4h10v5a5 5 0 0 1-10 0z" />
                <path d="M7 6H4.5a1 1 0 0 0-1 1v1a3 3 0 0 0 3 3H7M17 6h2.5a1 1 0 0 1 1 1v1a3 3 0 0 1-3 3H17" />
                <path d="M12 14v3M8.5 20.5h7M10 17h4v3.5h-4z" />
            </>,
        ),
    qualification: () =>
        svg(
            <>
                <circle cx="12" cy="9" r="5.5" />
                <path d="m8.5 13.5-1.5 7 5-2.5 5 2.5-1.5-7" />
            </>,
        ),
    checkIn: () =>
        svg(
            <>
                <circle cx="12" cy="12" r="8.5" />
                <path d="m8.5 12.5 2.5 2.5 5-5.5" />
            </>,
        ),
    media: () =>
        svg(
            <>
                <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
                <circle cx="12" cy="13" r="3.5" />
            </>,
        ),
    contracts: () =>
        svg(
            <>
                <path d="M14 3.5H6.5A1.5 1.5 0 0 0 5 5v14a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 19V8.5z" />
                <path d="M14 3.5V8.5h5" />
                <path d="M8.5 16.5c1-1.8 2-1.8 2.5 0s1.5 1.8 2.5 0" />
            </>,
        ),
    reviews: () => svg(<path d="m12 3.5 2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L12 16.9l-5.3 2.8 1.1-5.9-4.3-4.1 5.9-.8z" />),
}

export const MenuIcon = ({ name }) => {
    const Icon = ORG_MENU_ICONS[name]
    return Icon ? <Icon /> : null
}

export const CloseIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        aria-hidden="true"
    >
        <path d="M6 6l12 12M18 6 6 18" />
    </svg>
)

/** 14 × 7 chevron (group rows); `small` = the 12 × 6 one of the Contracts sub-blocks. */
export const Chevron = ({ small = false }) => (
    <svg
        className={small ? 'osd-chev2' : 'osd-chev'}
        viewBox="0 0 14 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="m1 1 6 5 6-5" />
    </svg>
)
